import type {
  IGetRecordResponse,
  IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  resolveSequentially,
  HISTORY_DATE_FORMAT,
  sortByCreatedAt,
  sortTimestampAsc,
  Timestamp,
  Timezone,
  toISODate,
  toMoment,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type INamedDocument,
  type WithRef,
  isISODateType,
} from '@principle-theorem/shared';
import {
  getDefaultTreatmentPlanName,
  IPatientTreatmentPlanJobData,
  IStepWithIdentifiers,
  ITreatmentPlanStepPair,
} from '../../../../destination/entities/patient-treatment-plans';
import { TranslationMapHandler } from '../../../../translation-map';
import {
  AppointmentSourceEntity,
  ExactAppointment,
  ExactAppointmentStatus,
} from '../../../../exact/source/entities/patient-appointment';
import {
  ExactTreatment,
  type IExactTreatment,
} from '../../../../exact/source/entities/patient-treatments';
import { compact, first, groupBy, sortBy, uniqBy, upperFirst } from 'lodash';
import {
  CalculateTreatmentWeight,
  ChartedItemScopeResolver,
  ChartedSurface,
  ChartedTreatment,
  determineTaxFromStrategy,
  FeeSchedule,
  PricedServiceCodeEntry,
  stafferToNamedDoc,
  TreatmentPlan,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  ChartableSurface,
  TreatmentPlanStatus,
  TreatmentStepStatus,
  type IChartedSurface,
  type IChartedTreatment,
  type IFeeSchedule,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import * as moment from 'moment-timezone';
import { resolveFeeSchedule } from '../../../../mappings/fee-schedules';
import { ExactAppointmentMatcher } from '../../../../exact/destination/entities/lib/exact-appointment-matcher';
import { TaxStrategy, roundTo2Decimals } from '@principle-theorem/accounting';
import {
  determineToothSurfaceByScope,
  resolveMappedCodeTreatmentPair,
  resolveMappTreatmentConfiguration,
} from '../../../../mappings/item-codes';
import { getResolvedTreatmentData } from '../../../../exact/util/helpers';
import { getExactChartedRefs } from '../../../../exact/util/tooth';
import { resolveExactStaffer } from '../../../../exact/destination/mappings/staff';
import { PracticeWorksPatient } from '../../../source/entities/patients';

export class PracticeWorksTreatmentPlanBuilder {
  static getPlanIdentifier(sourcePatientId: string, planId?: string): string {
    if (planId) {
      return `${sourcePatientId}-plan-${planId}`;
    }
    return `${sourcePatientId}-plan`;
  }

  static async buildTreatmentPlanData(
    data: IPatientTreatmentPlanJobData<PracticeWorksPatient>,
    translationMap: TranslationMapHandler,
    sourceTreatments: IGetRecordResponse<ExactTreatment>[],
    migration: WithRef<IPracticeMigration>,
    sourcePatientId: string,
    sourceAppointments: IGetRecordResponse<ExactAppointment>[],
    appointmentEntity: AppointmentSourceEntity
  ): Promise<ITreatmentPlanStepPair[]> {
    const planGroups = groupBy(
      sourceTreatments,
      (sourceTreatment) => sourceTreatment.data.treatment_plan_id ?? 'default'
    );

    const defaultFeeSchedule = await FeeSchedule.getPreferredOrDefault(
      migration.configuration.organisation
    );

    const plans: ITreatmentPlanStepPair[] = [];

    const appointmentMatcher = new ExactAppointmentMatcher(
      sourceAppointments.sort((appointmentA, appointmentB) =>
        sortTimestampAsc(
          appointmentA.record.translations.appointmentDateTime,
          appointmentB.record.translations.appointmentDateTime
        )
      ),
      (appointment) =>
        toISODate(
          appointment.record.translations.appointmentDateTime,
          migration.configuration.timezone
        )
    );

    await resolveSequentially(Object.values(planGroups), async (treatments) => {
      const planId = treatments[0].data.treatment_plan_id ?? undefined;
      const planIdentifier =
        PracticeWorksTreatmentPlanBuilder.getPlanIdentifier(
          sourcePatientId,
          planId
        );

      const createdAt = treatments[0].record.translations.plannedDate
        ? toTimestamp(
            toMomentTz(
              treatments[0].record.translations.plannedDate,
              migration.configuration.timezone
            )
          )
        : undefined;

      const defaultPlanName = createdAt
        ? `Migrated Treatment Plan ${toISODate(createdAt)}`
        : getDefaultTreatmentPlanName(migration);

      const planName =
        treatments[0].data.treatment_description ?? defaultPlanName;

      const practitioner = await resolveExactStaffer(
        treatments[0].data.provider_code ?? '',
        translationMap,
        data.staff
      );

      let planSchedule: INamedDocument<IFeeSchedule> | undefined;
      if (treatments[0].data.payment_plan_id) {
        const scheduleResolve = resolveFeeSchedule(
          translationMap,
          defaultFeeSchedule
        );
        planSchedule = await scheduleResolve(
          treatments[0].data.payment_plan_id
        );
      }

      const plan = {
        ...TreatmentPlan.init({
          name: planName.trim(),
          practitioner: practitioner
            ? stafferToNamedDoc(practitioner)
            : undefined,
          createdAt,
          feeSchedule: planSchedule,
        }),
        sourceIdentifier: planIdentifier,
      };

      const completedSteps =
        await PracticeWorksTreatmentPlanBuilder.buildTreatmentStepData(
          treatments,
          translationMap,
          data,
          migration,
          sourcePatientId,
          defaultFeeSchedule,
          appointmentEntity,
          appointmentMatcher,
          {
            filterFn: (treatment) =>
              !!treatment.record.translations.completedDate,
            groupByField: 'completedDate',
            defaultStepName: (date) => `Completed Step - ${date}`,
            defaultStatus: TreatmentStepStatus.Complete,
          }
        );
      const incompleteSteps =
        await PracticeWorksTreatmentPlanBuilder.buildTreatmentStepData(
          treatments,
          translationMap,
          data,
          migration,
          sourcePatientId,
          defaultFeeSchedule,
          appointmentEntity,
          appointmentMatcher,
          {
            filterFn: (treatment) =>
              !!treatment.record.translations.plannedDate &&
              !treatment.record.translations.completedDate,
            groupByField: 'plannedDate',
            defaultStepName: (date) => `Planned On - ${date}`,
            defaultStatus: TreatmentStepStatus.Incomplete,
          }
        );
      const steps = [
        ...sortBy(completedSteps, (step) => step.date),
        ...sortBy(incompleteSteps, (step) => step.date),
      ];

      if (!steps.length) {
        return;
      }

      const earliestStepDate = first(steps.sort(sortByCreatedAt))?.createdAt;
      if (earliestStepDate) {
        plan.createdAt = earliestStepDate;
      }

      const status = PracticeWorksTreatmentPlanBuilder.determinePlanStatus(
        steps,
        plan.createdAt
      );

      plans.push({
        plan: {
          ...plan,
          status,
        },
        steps,
      });
    });

    return plans;
  }

  static async getStepIdentifier(
    migration: WithRef<IPracticeMigration>,
    appointmentEntity: AppointmentSourceEntity,
    sourcePatientId: string,
    timezone: Timezone,
    treatment?: IGetRecordResponse<ExactTreatment>,
    appointment?: IGetRecordResponse<ExactAppointment>,
    patientAppointments?: IGetRecordResponse<ExactAppointment>[]
  ): Promise<string> {
    if (treatment) {
      const completedDate = treatment.record.translations.completedDate;
      if (completedDate) {
        return `${sourcePatientId}-${completedDate}`;
      }

      const plannedDate = treatment.record.translations.plannedDate;
      if (!plannedDate) {
        throw new Error(
          `No completed or planned date provided in treatment ${treatment.record.uid}`
        );
      }

      return `${sourcePatientId}-${plannedDate}`;
    }

    if (!appointment) {
      throw new Error('No appointment or treatment provided');
    }

    const appointmentDate = toISODate(
      appointment.record.translations.appointmentDateTime,
      timezone
    );
    const sourceAppointments =
      patientAppointments ??
      (await appointmentEntity.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      ));
    const sameDayAppointments = sourceAppointments.filter(
      (sourceAppointment) =>
        toISODate(
          sourceAppointment.record.translations.appointmentDateTime,
          timezone
        ) === appointmentDate
    );

    const sorted = [...sameDayAppointments].sort((appointmentA, appointmentB) =>
      sortTimestampAsc(
        appointmentA.record.translations.appointmentDateTime,
        appointmentB.record.translations.appointmentDateTime
      )
    );

    const appointmentIndex = sorted.findIndex(
      (sortedAppointment) =>
        sortedAppointment.record.uid === appointment.record.uid
    );

    if (appointmentIndex < 1) {
      return `${sourcePatientId}-${appointmentDate}`;
    }
    return `${sourcePatientId}-${appointmentDate}-${appointmentIndex + 1}`;
  }

  static determinePlanStatus(
    steps: IStepWithIdentifiers[],
    planCreatedAt: Timestamp
  ): TreatmentPlanStatus {
    const allComplete = steps.every(
      (step) => step.status === TreatmentStepStatus.Complete
    );
    if (allComplete && steps.length) {
      return TreatmentPlanStatus.Completed;
    }

    const planAgeInMonths = moment().diff(toMoment(planCreatedAt), 'months');
    const someComplete = steps.some(
      (step) => step.status === TreatmentStepStatus.Complete
    );
    if (someComplete) {
      return TreatmentPlanStatus.InProgress;
    }

    const allIncomplete = steps.every(
      (step) => step.status === TreatmentStepStatus.Incomplete
    );
    if (allIncomplete && planAgeInMonths > 12) {
      return TreatmentPlanStatus.Declined;
    }

    return TreatmentPlanStatus.Draft;
  }

  static async getChartedTreatments(
    sourceTreatments: IExactTreatment[],
    getPreferredOrDefaultRef: (
      feeSheduleId?: string
    ) => Promise<INamedDocument<IFeeSchedule>>,
    data: IPatientTreatmentPlanJobData<PracticeWorksPatient>,
    translationMap: TranslationMapHandler,
    timezone: Timezone
  ): Promise<IChartedTreatment[]> {
    const treatments: IChartedTreatment[] = [];
    await resolveSequentially(sourceTreatments, async (sourceTreatment) => {
      const practitioner = await resolveExactStaffer(
        sourceTreatment.provider_code ?? '',
        translationMap,
        data.staff
      );
      if (!practitioner) {
        throw new Error(
          `Couldn't resolve practitioner for treatment ${sourceTreatment.provider_code}`
        );
      }

      const [code, mappedTreatment] = resolveMappedCodeTreatmentPair(
        data.sourceItemCodes,
        data.treatmentConfigurationMappings,
        sourceTreatment.service_code,
        sourceTreatment.service_code
      );
      if (!code && !mappedTreatment) {
        // eslint-disable-next-line no-console
        console.log(
          `Couldn't resolve code or treatment from: ${sourceTreatment.service_code}`
        );
        return;
      }

      const treatmentConfiguration = await resolveMappTreatmentConfiguration(
        data.defaultTreatmentConfiguration,
        mappedTreatment
      );

      const sourceFeeScheduleId = sourceTreatment.payment_plan_id ?? undefined;
      const feeSchedule = await getPreferredOrDefaultRef(sourceFeeScheduleId);
      const taxStatus = TaxStrategy.GSTFree;
      const price = roundTo2Decimals(sourceTreatment.total_amount);
      const scopeResolver = new ChartedItemScopeResolver();

      const chartedSurfaces =
        PracticeWorksTreatmentPlanBuilder.getChartedSurfaces(
          sourceTreatment,
          practitioner,
          timezone
        ).map((chartedSurface) =>
          determineToothSurfaceByScope(
            chartedSurface,
            treatmentConfiguration,
            scopeResolver
          )
        );

      const serviceCode = code
        ? PricedServiceCodeEntry.init({
            code: code.code,
            type: code.type,
            taxStatus,
            price,
            tax: determineTaxFromStrategy(data.taxRate, {
              taxStatus: taxStatus,
              amount: price,
            }),
            chartedSurfaces,
          })
        : undefined;

      const scopeRefs = scopeResolver.reduceChartedSurfacesToScope(
        treatmentConfiguration,
        chartedSurfaces
      );
      const scopeRef = first(scopeRefs)?.scopeRef ?? {
        scope: ChartableSurface.WholeMouth,
      };

      const resolvedTreatmentData = getResolvedTreatmentData(
        sourceTreatment,
        practitioner,
        timezone
      );

      const serviceCodes = serviceCode ? [serviceCode] : [];
      const basePrice = serviceCodes.length ? 0 : price;

      treatments.push(
        ChartedTreatment.init({
          uuid: sourceTreatment.treatment_id,
          config: toNamedDocument(treatmentConfiguration),
          feeSchedule: toNamedDocument(feeSchedule),
          chartedSurfaces: chartedSurfaces,
          attributedTo: stafferToNamedDoc(practitioner),
          serviceCodes,
          basePrice,
          scopeRef,
          resolvedAt: resolvedTreatmentData?.resolvedAt,
          resolvedBy: resolvedTreatmentData?.resolvedBy,
        })
      );
    });

    return treatments;
  }

  static getChartedSurfaces(
    sourceTreatment: IExactTreatment,
    practitioner: WithRef<IStaffer>,
    timezone: Timezone
  ): IChartedSurface[] {
    if (!sourceTreatment.tooth_range && !sourceTreatment.tooth) {
      return [];
    }

    const teeth =
      sourceTreatment.tooth_range ?? compact([sourceTreatment.tooth]);
    return teeth
      .map((tooth) =>
        getExactChartedRefs(tooth, sourceTreatment.tooth_surfaces).map(
          (chartedRef) => {
            const resolvedTreatmentData = getResolvedTreatmentData(
              sourceTreatment,
              practitioner,
              timezone
            );

            return ChartedSurface.init({
              chartedBy: stafferToNamedDoc(practitioner),
              resolvedAt: resolvedTreatmentData?.resolvedAt,
              resolvedBy: resolvedTreatmentData?.resolvedBy,
              chartedRef,
            });
          }
        )
      )
      .flat();
  }

  static async buildTreatmentStepData(
    treatments: IGetRecordResponse<ExactTreatment>[],
    translationMap: TranslationMapHandler,
    data: IPatientTreatmentPlanJobData<PracticeWorksPatient>,
    migration: WithRef<IPracticeMigration>,
    sourcePatientId: string,
    defaultFeeSchedule: INamedDocument<IFeeSchedule>,
    appointmentEntity: AppointmentSourceEntity,
    appointmentMatcher: ExactAppointmentMatcher,
    config: {
      filterFn: (treatment: IGetRecordResponse<ExactTreatment>) => boolean;
      groupByField: 'plannedDate' | 'completedDate';
      defaultStepName: (date: string) => string;
      defaultStatus: TreatmentStepStatus;
    }
  ): Promise<IStepWithIdentifiers[]> {
    const steps: IStepWithIdentifiers[] = [];
    const filteredTreatments = treatments.filter(config.filterFn);
    const groupedByDate = groupBy(
      filteredTreatments,
      (treatment) => treatment.record.translations[config.groupByField]
    );

    await resolveSequentially(
      Object.entries(groupedByDate),
      async ([date, stepGroup]) => {
        if (!isISODateType(date)) {
          throw new Error(`Invalid ${config.groupByField}: ${date as string}`);
        }

        const sourceTreatments = stepGroup.map((response) => response.data);
        const chartedTreatments =
          await PracticeWorksTreatmentPlanBuilder.getChartedTreatments(
            sourceTreatments,
            resolveFeeSchedule(translationMap, defaultFeeSchedule),
            data,
            translationMap,
            migration.configuration.timezone
          );
        if (!chartedTreatments.length) {
          return;
        }

        let stepName = config.defaultStepName(date);
        const createdAt = toTimestamp(
          toMomentTz(date, migration.configuration.timezone)
        );
        let status = config.defaultStatus;
        const matchingAppointment = appointmentMatcher.findMatch(date);
        if (matchingAppointment) {
          appointmentMatcher.removeMatch(matchingAppointment);

          stepName = matchingAppointment.data.appointment_note?.length
            ? matchingAppointment.data.appointment_note
            : `${upperFirst(
                matchingAppointment.data.appointment_status
              )} - ${toMomentTz(
                matchingAppointment.record.translations.appointmentDateTime,
                migration.configuration.timezone
              ).format(HISTORY_DATE_FORMAT)}`;

          const appointmentStatus = convertExactAppointmentStatus(
            matchingAppointment.data.appointment_status
          );
          status = appointmentStatusToTreatmentStepStatus(appointmentStatus);
        }

        const stepIdentifier =
          await PracticeWorksTreatmentPlanBuilder.getStepIdentifier(
            migration,
            appointmentEntity,
            sourcePatientId,
            migration.configuration.timezone,
            stepGroup[0]
          );

        const existingStep = steps.find(
          (step) => step.sourceIdentifier === stepIdentifier
        );
        if (existingStep) {
          existingStep.treatments = uniqBy(
            [...existingStep.treatments, ...chartedTreatments],
            (treatment) => treatment.uuid
          );
          return;
        }

        const step = {
          ...TreatmentStep.init({
            name: stepName,
            treatments: chartedTreatments,
            status,
          }),
          createdAt,
        };

        const treatmentCategory =
          await CalculateTreatmentWeight.getPrimaryCategory(
            step,
            data.treatmentCategories
          );

        steps.push({
          ...step,
          display: {
            ...step.display,
            primaryTreatmentCategory: treatmentCategory?.ref,
          },
          createdAt,
          sourceIdentifier: stepIdentifier,
          date,
        });
      }
    );
    return steps;
  }
}

export function convertExactAppointmentStatus(
  status: ExactAppointmentStatus
): AppointmentStatus {
  switch (status) {
    case ExactAppointmentStatus.Booked:
      return AppointmentStatus.Scheduled;
    case ExactAppointmentStatus.Cancelled:
    case ExactAppointmentStatus.Failed:
      return AppointmentStatus.Cancelled;
    case ExactAppointmentStatus.Complete:
      return AppointmentStatus.Complete;
    case ExactAppointmentStatus.Confirmed:
      return AppointmentStatus.Confirmed;
    default:
      return AppointmentStatus.Scheduled;
  }
}

function appointmentStatusToTreatmentStepStatus(
  status: AppointmentStatus
): TreatmentStepStatus {
  switch (status) {
    case AppointmentStatus.Complete:
      return TreatmentStepStatus.Complete;
    default:
      return TreatmentStepStatus.Incomplete;
  }
}
